import { useState } from 'react';

import { useJobStatus, useRecomputeObservations, type RecomputeStats } from '../view-models/observations.ts';

/**
 * "Recompute ratings" button — enqueues a fresh derive + score pass
 * via the JOBS_QUEUE and polls the job status until it terminates.
 * Used on the settings pages so an owner can refresh the matrix
 * after editing clusters or derivedContexts without launching a full
 * onboarding run.
 *
 * Lifecycle: idle → enqueue (202 with job_id) → polling every 3s →
 * terminal (success/error). The result pill persists until the user
 * dismisses it; the matrix view caches are invalidated when the
 * job's status flips to 'succeeded' so navigating back to
 * /resilience shows fresh data.
 */
export function RecomputeRatingsButton({ orgSlug }: { orgSlug: string | null }) {
  const enqueue = useRecomputeObservations(orgSlug);
  const [jobId, setJobId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const status = useJobStatus(orgSlug, jobId);

  const onClick = () => {
    setDismissed(false);
    setJobId(null);
    enqueue.mutate(undefined, {
      onSuccess: (data) => setJobId(data.job_id),
    });
  };

  const job = status.data;
  const isRunning =
    enqueue.isPending || job?.status === 'queued' || job?.status === 'running';
  const isSucceeded = job?.status === 'succeeded';
  const isFailed = job?.status === 'failed' || enqueue.isError;
  const showResult = !dismissed && (isSucceeded || isFailed);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
      <button type="button" onClick={onClick} disabled={isRunning || orgSlug === null}>
        {isRunning ? '⏳ Recomputing…' : 'Recompute ratings'}
      </button>
      {showResult && isSucceeded ? (
        <ResultPill kind="success" onDismiss={() => setDismissed(true)}>
          <SuccessText resultJson={job!.result_json} />
        </ResultPill>
      ) : null}
      {showResult && isFailed ? (
        <ResultPill kind="error" onDismiss={() => setDismissed(true)}>
          ✗ {job?.error_message ?? (enqueue.error instanceof Error ? enqueue.error.message : 'Recompute failed')}
        </ResultPill>
      ) : null}
    </div>
  );
}

function SuccessText({ resultJson }: { resultJson: string | null }) {
  if (!resultJson) return <>✓ Recompute complete — matrix views will refresh on next visit</>;
  let stats: RecomputeStats | null = null;
  try {
    stats = JSON.parse(resultJson) as RecomputeStats;
  } catch {
    return <>✓ Recompute complete — matrix views will refresh on next visit</>;
  }
  return (
    <>
      ✓ Wrote {stats.observations_inserted.toLocaleString()} observations ·{' '}
      {stats.scores_inserted.toLocaleString()} scores · {stats.engineers} engineers ·
      ({(stats.duration_ms / 1000).toFixed(1)}s) — matrix views will refresh on next visit
    </>
  );
}

function ResultPill({
  kind,
  onDismiss,
  children,
}: {
  kind: 'success' | 'error';
  onDismiss: () => void;
  children: React.ReactNode;
}) {
  const bg = kind === 'success' ? 'rgba(40, 160, 80, 0.12)' : 'rgba(200, 50, 50, 0.12)';
  const color = kind === 'success' ? 'var(--color-fg, inherit)' : 'var(--color-danger, #c33)';
  return (
    <span
      style={{
        background: bg,
        color,
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.85rem',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <span>{children}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '1rem',
          lineHeight: 1,
          padding: '0 0.25rem',
          color: 'inherit',
        }}
      >
        ×
      </button>
    </span>
  );
}
